/**
 * Fold state of the rule cards in a block settings panel (Workflow overhaul P4, 2026-10-10).
 *
 * The state is the panel's OWN UI state: it lives in the mixin's `foldState` and is never written into
 * the node being edited (a saved workflow must not gain a field). A rule has no id of its own, so a rule
 * object is identified by a key kept in a WeakMap: the same object is the same rule for as long as it
 * exists, whatever its index becomes after another rule is removed.
 *
 * The rule from the design: with ONE rule it starts open; with several they start folded, except a rule
 * that was just added (open). "Expand all" / "Collapse all" sets every rule of a list at once.
 */
const KEYS = new WeakMap();
let sequence = 0;

/** A stable key for a rule object ('' for anything that is not an object). */
export function ruleKey(rule) {
  if (rule === null || typeof rule !== 'object') return '';
  let key = KEYS.get(rule);
  if (!key) {
    sequence += 1;
    key = `rule-${sequence}`;
    KEYS.set(rule, key);
  }
  return key;
}

export const ruleFold = {
  data() {
    return { foldState: {} };
  },

  methods: {
    ruleKey,

    /** Is this rule open? `count` is the number of rules in its list (a lone rule starts open). */
    ruleOpen(rule, count) {
      const state = this.foldState[ruleKey(rule)];
      return state === undefined ? count === 1 : state;
    },

    toggleRule(rule, count) {
      this.$set(this.foldState, ruleKey(rule), !this.ruleOpen(rule, count));
    },

    /** True when the list has rules and every one is open. */
    allRulesOpen(rules) {
      return rules.length > 0 && rules.every(rule => this.ruleOpen(rule, rules.length));
    },

    setAllRules(rules, open) {
      rules.forEach(rule => this.$set(this.foldState, ruleKey(rule), open));
    },

    /** Expand all when something is folded, else collapse all. */
    toggleAllRules(rules) {
      this.setAllRules(rules, !this.allRulesOpen(rules));
    },

    /**
     * Run the classic "add rule" method and open only the rule it added: the others fold, so the new
     * one is the thing on screen. `read()` returns the rules array as it is NOW (the classic method may
     * create the array on first use, so a reference taken before the call can be stale).
     */
    addRuleAndOpen(read, add) {
      const before = read().slice();
      add();
      const after = read();
      this.setAllRules(before, false);
      const added = after.filter(rule => before.indexOf(rule) === -1);
      added.forEach(rule => this.$set(this.foldState, ruleKey(rule), true));
    },
  },
};
